<template>
  <div class="case-creation__card mb-15">
    <div v-if="hasMissingTubes" class="missing-tubes-alert">
      <strong>Not Collected tubes:</strong>
      <span>{{ missingTubes.join(", ") }}</span>
    </div>
    <div class="case-creation__heading">
      <div class="title-with-icon">
        <span class="icon">
          <img
            src="@/assets/images/icons/icon-test-info.svg"
            alt="icon"
            class="img-fluid"
          />
        </span>
        <h3 class="title">TEST TUBES INFORMATION</h3>
        <button
          v-if="isMlxRecollectionAllowed && hasEditablePendingIssues"
          type="button"
          class="edit-pending-issues-btn"
          :class="{ 'is-active': isPendingIssueEditMode }"
          :disabled="isEditActionLoading"
          @click="enablePendingIssueEdit"
        >
          <img
            src="@/assets/images/icons/actions/edit-active.svg"
            alt="edit pending issues"
            class="edit-pending-issues-icon"
          />
          <span class="edit-pending-issues-text">
            {{ isPendingIssueEditMode ? "Edit Mode" : "Edit Pending Issues" }}
          </span>
        </button>
        <button
          v-if="isPendingIssueEditMode"
          type="button"
          class="edit-pending-issues-btn cancel-pending-issues-btn"
          :disabled="isEditActionLoading"
          @click="cancelPendingIssueEdit"
        >
          <span class="edit-pending-issues-text">Cancel</span>
        </button>
      </div>
    </div>
    <div
      v-loading="isEditActionLoading"
      :element-loading-text="editLoadingText"
      element-loading-spinner="el-icon-loading"
      class="case-creation__body"
    >
      <div class="billing-information-tabs">
        <div class="tube-scroll-wrapper">
          <div v-if="tubeInfo.length">
            <div v-if="isMlxRecollectionAllowed" class="tube-notify-message">
              Before proceeding with re collection, please change the status to
              <strong>Not Collected</strong> or <strong>Hemolyzed</strong>, and
              remove tests that do not need re collection.
            </div>
            <div class="tube-header">
              <div class="tube-column">Test Tube</div>
              <div class="tube-column">Status</div>
              <div class="tube-column">Test Information</div>
              <div class="tube-column">Sample Acknowledge</div>
              <div class="tube-column">Acknowledged By</div>
            </div>

            <div class="tube-cards-block">
              <div
                class="tube-card"
                v-for="(tube, index) in tubeInfo"
                :key="tube._id"
              >
                <div class="tube-column img-block">
                  <div class="test-tube-img-block">
                    <!-- Use Element Plus el-image with built-in lazy loading -->
                    <el-image
                      :src="getDownloadUrl(tube.image_url)"
                      alt="Test Tube"
                      fit="contain"
                      lazy
                      style="width: 100%; height: 100%"
                    />
                  </div>
                  <p class="tube-name">{{ tube?.tube_name }}</p>
                </div>

                <template v-if="tube.tests && tube.tests.length">
                  <div class="tube-column">
                    <el-select
                      v-model="dropdownStatus[index]"
                      placeholder="Select Status"
                      size="small"
                      @change="handleStatusChange(index, $event)"
                    >
                      <el-option label="Collected" value="Collected" />
                      <el-option label="Not Collected" value="Not Collected" />
                      <el-option label="Hemolyzed" value="Hemolyzed" />
                      <el-option
                        v-if="getTotalDropdownOptions(tube) > 1"
                        label="Partially Collected"
                        value="Partially Collected"
                      />
                    </el-select>
                    <el-tooltip
                      v-if="isRecollectionStatus(dropdownStatus[index])"
                      content="Flagged for re collection — excluded from Create Accession"
                      placement="top"
                    >
                      <span class="recollection-chip">RE-COLLECTION</span>
                    </el-tooltip>
                  </div>
                  <div class="tube-column">
                    <el-select
                      v-model="selectCategory[index]"
                      placeholder="Select Category"
                      multiple
                      size="small"
                      collapse-tags
                      style="width: 200px !important"
                      @change="handleSelectionChange(index)"
                      @blur="validateSelection(index)"
                    >
                          <el-option
                          v-for="test in getAllTestOptions(tube)"
                          :key="test"
                          :label="getTestOptionLabel(tube, test)"
                          :value="test"
                          :disabled="isTestDisabled(tube, test)"
                        />
                      </el-select>
                      <button
                        v-if="!isPendingIssueEditMode && selectCategory[index] && selectCategory[index].length > 0"
                        type="button"
                        class="clear-all-tests-btn"
                        @click="clearTubeTests(index)"
                      >Clear All</button>
                      <p v-if="selectErrors[index]" class="error-text">
                        {{ selectErrors[index] }}
                      </p>
                      <p
                        v-if="getInhouseOtherLabTests(tube).length"
                        class="inhouse-note"
                      >
                        {{ getInhouseOtherLabTests(tube).join(', ') }} — In-house at another lab
                      </p>
                  </div>
                  <div class="tube-column ack-status-column">
                    <el-select
                      v-model="ackStatus[index]"
                      placeholder="Select Status"
                      size="small"
                      @change="onAckStatusChange"
                    >
                      <el-option label="Received" value="RECEIVED" />
                      <el-option label="Not Received" value="NOT_RECEIVED" />
                    </el-select>
                    <el-select
                      v-model="sendoutLab[index]"
                      placeholder="Select Lab"
                      clearable
                      filterable
                      size="small"
                      class="ack-lab-select"
                      @focus="fetchAllSendoutLabs"
                    >
                      <el-option
                        v-for="lab in getAllLabs"
                        :key="lab._id"
                        :label="lab.name"
                        :value="lab.name"
                      ></el-option>
                    </el-select>
                  </div>
                  <div class="tube-column ack-by-column">
                    <span v-if="getAckByText(tube)" class="ack-by-text">{{ getAckByText(tube) }}</span>
                  </div>
                </template>
              </div>
            </div>

            <div class="tube-actions-row" v-if="tubeInfo.length && !isClientSalesUser">
              <el-button
                class="ack-confirm-btn"
                :loading="isAckSaving"
                @click="confirmAcknowledgment"
              >
                CONFIRM ACKNOWLEDGMENT
              </el-button>
              <el-button
                v-if="isMlxRecollectionAllowed && !hasSamplePickupId && hasCollectedTests"
                class="recollection-btn-all"
                :loading="isRecollectionLoading"
                :disabled="!currentOrderId || isRecollectionLoading || !hasValidTubes || isPendingIssueEditMode"
                @click="handleRecollectionAll"
              >
                CONFRIM-RECOLLECTION
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Re Collection Confirmation Dialog -->
  <div class="default-modal">
    <el-dialog
      v-model="showRecollectionConfirm"
      top="10%"
      width="40%"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      :before-close="cancelRecollectionConfirm"
      class="recollection-theme-modal"
    >
      <div class="dialog-header">
        <span class="header-part">
          <img
            src="@/assets/images/icons/icon-test-info.svg"
            alt="icon"
            class="dialog-header-icon"
          />
          Re Collection Confirmation
        </span>
        <el-button
          size="medium"
          class="el-white"
          type="info"
          plain
          @click="cancelRecollectionConfirm"
        >
          <img src="@/assets/images/icons/cases/close-btn.svg" alt="close" />
        </el-button>
      </div>

      <div class="recollection-confirm-body">
        <p class="recollection-confirm-msg">The following tubes are <strong>selected and ready to recollect</strong>. Please confirm to proceed.</p>
        <div class="recollection-preview-list">
          <div
            v-for="(item, i) in recollectionPreview"
            :key="i"
            class="recollection-preview-item"
          >
            <div class="preview-row">
              <span class="preview-label">Tube</span>
              <span class="preview-value">{{ item.tubeName }}</span>
            </div>
            <div class="preview-row">
              <span class="preview-label">Status</span>
              <span class="preview-value preview-status">{{ item.status }}</span>
            </div>
            <div class="preview-row">
              <span class="preview-label">Tests</span>
              <span class="preview-value">{{ item.tests.join(', ') }}</span>
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="recollection-confirm-footer dialog-footer">
          <el-button class="el-white" @click="cancelRecollectionConfirm">Cancel</el-button>
          <el-button
            class="blue-save-button"
            :loading="isRecollectionLoading"
            @click="confirmRecollection"
          >Confirm</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { errorNotification, successNotification } from "@/helpers/notifications";
import { mapGetters } from "vuex";
import UserPermissionsHelper from "@/mixins/UserPermissionsHelper";

const TUBE_STATUS = {
  COLLECTED: "Collected",
  NOT_COLLECTED: "Not Collected",
  PARTIALLY_COLLECTED: "Partially Collected",
  HEMOLYZED: "Hemolyzed",
};

export default {
  name: "LabsquireTestTubesInfo",
  mixins: [UserPermissionsHelper],
  data() {
    return {
      TUBE_STATUS,
      dropdownStatus: [],
      selectCategory: [],
      hasUserInteracted: [],
      selectErrors: [],
      ackStatus: [],
      ackOriginalStatus: [],
      sendoutLab: [],
      isAckSaving: false,
      // Tube ids in the same order as the selection arrays above, so that a
      // re-initialization can preserve the user's in-progress choices per tube.
      tubeIdOrder: [],
      ackPendingConfirmation: false,
      isRecollectionLoading: false,
      showRecollectionConfirm: false,
      recollectionPreview: [],
      isEditActionLoading: false,
      editLoadingText: "Reverting...",
      isPendingIssueEditMode: false,
      pendingIssueEditSnapshot: {},
      suppressWatcher: false,
      debounceTimer: null, // For debouncing emitTubeData
    };
  },
  computed: {
    ...mapGetters("orders", ["getCaseInformation", "getTestTubeInfoById"]),
    ...mapGetters("labs", ["getAllLabs"]),
    isMlxRecollectionAllowed() {
      const orderType = String(this.getCaseInformation?.order_type || "").toUpperCase();
      return orderType === "MLX" || (Boolean(this.getCaseInformation?.sent_to_mlx) && this.getCaseInformation.collected_tests && this.getCaseInformation.collected_tests.length) || (Boolean(this.getCaseInformation?.is_phlebo_order));
    },
    pendingIssues() {
      return (this.getCaseInformation?.samples_with_issues || []).filter(
        (issue) => issue?.status?.toUpperCase() === "PENDING"
      );
    },
    pendingIssueStateByTube() {
      return this.pendingIssues.reduce((accumulator, issue) => {
        const tubeName = String(issue?.tube_name || "").trim().toLowerCase();
        if (!tubeName) {
          return accumulator;
        }

        if (!accumulator[tubeName]) {
          accumulator[tubeName] = {
            tests: new Set(),
            issues: [],
            reason: issue?.reason || this.TUBE_STATUS.NOT_COLLECTED,
          };
        }

        (issue?.tests || []).forEach((test) => {
          if (test) {
            accumulator[tubeName].tests.add(test);
          }
        });
        accumulator[tubeName].issues.push(issue);

        if (!accumulator[tubeName].reason && issue?.reason) {
          accumulator[tubeName].reason = issue.reason;
        }

        return accumulator;
      }, {});
    },
    hasEditablePendingIssues() {
      return this.isMlxRecollectionAllowed && Object.keys(this.pendingIssueStateByTube).length > 0;
    },
    hasSamplePickupId() {
      return Boolean(this.getCaseInformation?.sample_pickup_id);
    },
    hasCollectedTests() {
      return Boolean(this.getCaseInformation?.collected_tests && this.getCaseInformation.collected_tests.length);
    },
    missingTubes() {
      const missingTubes = this.getCaseInformation?.missing_tubes;
      if (!Array.isArray(missingTubes)) {
        return [];
      }

      return missingTubes
        .map((tubeName) => tubeName.toString().trim())
        .filter(Boolean);
    },
    hasMissingTubes() {
      return this.missingTubes.length > 0;
    },
    blockedTubeNames() {
      const issues = this.getCaseInformation?.samples_with_issues || [];
      const names = issues
        .filter((issue) => issue?.status?.toUpperCase() === 'REJECTED')
        .map((issue) => issue?.tube_name)
        .filter(Boolean)
        .map((name) => String(name).toLowerCase().trim());
      return new Set(names);
    },
    currentOrderId() {
      return this.getCaseInformation?.order_id || "";
    },
    hasValidTubes() {
      // Check if there is at least one tube with selected tests
      return this.tubeInfo.some((tube, index) => {
        return this.selectCategory[index] && this.selectCategory[index].length > 0;
      });
    },
    hasAckChanges() {
      return this.tubeInfo.some(
        (tube, index) => this.ackStatus[index] !== this.ackOriginalStatus[index]
      );
    },
    tubeInfo() {
      const restrictedOrderCodes = this.getCaseInformation?.restricted_order_codes;
      const isPendingRestricted =
        restrictedOrderCodes &&
        Array.isArray(restrictedOrderCodes.order_codes) &&
        restrictedOrderCodes.order_codes.length > 0 &&
        String(restrictedOrderCodes.status || "").toUpperCase() === "PENDING";
      const restrictedSet = isPendingRestricted
        ? new Set(restrictedOrderCodes.order_codes)
        : new Set();

      const convertedTestCodes =
        this.getCaseInformation?.accession_data?.flatMap(
          (item) => item?.test_codes || []
        ) || [];
      const allTubes = this.getTestTubeInfoById || [];
      return allTubes
        .map((tube) => {
          // Filter out tests already in convertedTestCodes and restricted order codes
          const remainingTests = (tube?.tests || []).filter(
            (test) =>
              !convertedTestCodes?.includes(test?.title) &&
              !restrictedSet.has(test?.title)
          );
          // Only return tube if it has remaining (non-converted) tests
          if (remainingTests?.length > 0) {
            return {
              ...tube,
              tests: remainingTests,
            };
          }

          // Otherwise, exclude this tube
          return null;
        })
        .filter(Boolean); // Remove nulls
    },
    selectedTubeInformation() {
      const sendoutGroups = {};
      const nonsendoutGroups = {};
      const allergyGroups = {};

      this.tubeInfo.forEach((tube, index) => {
        const tests = tube.tests || [];
        const status = this.dropdownStatus[index];
        const selectedTests = this.selectCategory[index] || [];

        if (selectedTests.length === 0) return;

        if (
          status === this.TUBE_STATUS.NOT_COLLECTED ||
          status === this.TUBE_STATUS.HEMOLYZED
        ) {
          return;
        }

        if (this.ackStatus[index] === "NOT_RECEIVED") {
          return;
        }

        tests.forEach((test) => {
          if (!selectedTests.includes(test.title)) return;

          const caseTypes = test.case_type || ["UNKNOWN"];

          caseTypes.forEach((caseType) => {
            // Allergy panels are in-house but grouped separately (like sendouts)
            const targetGroup = test.is_allergy
              ? allergyGroups
              : test.is_sendout
              ? sendoutGroups
              : nonsendoutGroups;

            if (!targetGroup[caseType]) {
              targetGroup[caseType] = {
                case_type: caseType,
                is_sendout: test.is_sendout,
                is_allergy: test.is_allergy || false,
                selected_tests: [],
                tubes: [],
              };
            }

            // Push the test title if not already present
            if (!targetGroup[caseType].selected_tests.includes(test.title)) {
              targetGroup[caseType].selected_tests.push(test.title);
            }

            // Track the tube (avoid duplicates)
            const existingTube = targetGroup[caseType].tubes.find(
              (t) => t.tube_id === tube._id
            );
            if (!existingTube) {
              targetGroup[caseType].tubes.push({
                tube_id: tube._id,
                tube_name: tube.tube_name,
                image_url: this.getDownloadUrl(tube.image_url),
                status: status,
              });
            }
          });
        });
      });

      // Convert grouped objects to arrays
      const formatGroups = (groupObj) => Object.values(groupObj);
      return {
        sendout: formatGroups(sendoutGroups),
        nonsendout: formatGroups(nonsendoutGroups),
        allergy: formatGroups(allergyGroups),
      };
    },
  },
  async mounted() {
    this.initTubeInfoWatcher();
    this.initSelectCategoryWatcher();
    this.initDropdownStatusWatcher();
    await this.fetchOrderTestsTubeInfo();
  },
  beforeUnmount() {
    // Clear debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },

  methods: {
    async fetchOrderTestsTubeInfo() {
      try {
        let orderId = this.$route.params.order_id;
        await this.$store.dispatch("orders/fetchOrderTestsTubeInfo", orderId);
      } catch (error) {
        console.error("Error fetching test tube info:", error);
      }
    },
    initTubeInfoWatcher() {
      this.$watch(
        () => this.tubeInfo,
        (newData) => {
          this.handleTubeInfoChange(newData);
        },
        { immediate: true }
      );
    },
    async handleTubeInfoChange(newData) {
      if (Array.isArray(newData)) {
        // Suppress the deep selectCategory watcher while we rebuild the arrays,
        // otherwise it would re-derive (and clobber) preserved statuses.
        this.suppressWatcher = true;
        this.initializeTubeSelections(newData);
        this.$nextTick(() => {
          this.suppressWatcher = false;
        });

        // Batch fetch presigned URLs in parallel for better performance
        const urlFetchPromises = [];

        for (const [index, tube] of newData.entries()) {
          const path = tube?.image_url?.split(".com/")[1];
          if (path) {
            // Check if URL already exists in cache before fetching
            const existingUrl = this.$store.getters["testTubesImageGeneration/getDownloadUrlByPath"](path);
            if (!existingUrl) {
              urlFetchPromises.push(
                this.$store.dispatch("testTubesImageGeneration/fetchDownloadUrl", path)
              );
            }
          }
        }

        // Fetch all URLs in parallel instead of sequentially
        if (urlFetchPromises.length > 0) {
          try {
            await Promise.all(urlFetchPromises);
          } catch (error) {
            console.error("Error fetching presigned URLs:", error);
          }
        }

        this.emitTubeData();

        if (this.isPendingIssueEditMode) {
          this.applyPendingIssueSelections();
        }
      }
    },
    initializeTubeSelections(tubes, { preserve = true } = {}) {
      // Snapshot the current selection/status keyed by tube id so a re-computed
      // tubeInfo (background refresh, cache change, re-render) doesn't wipe the
      // choices the user already made for the other tubes.
      const previousByTubeId = {};
      if (preserve) {
        this.tubeIdOrder.forEach((tubeId, i) => {
          previousByTubeId[tubeId] = {
            status: this.dropdownStatus[i],
            selectedTests: this.selectCategory[i],
            interacted: this.hasUserInteracted[i],
            error: this.selectErrors[i],
            ackStatus: this.ackStatus[i],
            sendoutLab: this.sendoutLab[i],
          };
        });
      }

      const nextStatus = [];
      const nextSelected = [];
      const nextInteracted = [];
      const nextErrors = [];
      const nextOrder = [];
      const nextAckStatus = [];
      const nextAckOriginal = [];
      const nextSendoutLab = [];

      tubes.forEach((tube) => {
        const selectableTitles = this.getSelectableTestsForTube(tube);
        const previous = previousByTubeId[tube._id];

        if (previous) {
          // Keep the user's prior choice, dropping any test no longer selectable.
          const preservedSelection = (previous.selectedTests || []).filter(
            (test) => selectableTitles.includes(test)
          );
          nextSelected.push(preservedSelection);
          nextStatus.push(previous.status || "Collected");
          nextInteracted.push(previous.interacted || false);
          nextErrors.push(previous.error || "");
        } else {
          nextSelected.push(selectableTitles);
          nextStatus.push("Collected");
          nextInteracted.push(false);
          nextErrors.push("");
        }
        nextOrder.push(tube._id);

        const normalizedAckStatus = this.normalizeAckStatus(tube.status);
        nextAckOriginal.push(normalizedAckStatus);
        nextAckStatus.push(previous?.ackStatus ?? normalizedAckStatus);
        nextSendoutLab.push(previous?.sendoutLab ?? tube.sendout_lab ?? "");
      });

      this.dropdownStatus = nextStatus;
      this.selectCategory = nextSelected;
      this.hasUserInteracted = nextInteracted;
      this.selectErrors = nextErrors;
      this.tubeIdOrder = nextOrder;
      this.ackStatus = nextAckStatus;
      this.ackOriginalStatus = nextAckOriginal;
      this.sendoutLab = nextSendoutLab;
    },
    initDropdownStatusWatcher() {
     
    },

    async handleStatusChange(index, status) {
      const tube = this.tubeInfo[index];
      if (!tube) return;

      this.hasUserInteracted[index] = true;
      this.suppressWatcher = true;

      if (status === this.TUBE_STATUS.COLLECTED) {
        const enabledTests = this.getSelectableTestsForTube(tube);
        this.selectCategory[index] = enabledTests;
        this.selectErrors[index] = "";
      } else if (status === this.TUBE_STATUS.NOT_COLLECTED) {
        // Keep existing selected tests — only "Clear All" removes them.
        this.selectErrors[index] = "";
      } else if (
        status === this.TUBE_STATUS.PARTIALLY_COLLECTED ||
        status === this.TUBE_STATUS.HEMOLYZED
      ) {
        this.selectErrors[index] = "";
      }

      this.suppressWatcher = false;
      this.emitTubeData();

      // In pending-issue edit mode the dedicated MLX flow handles updates.
      if (this.isPendingIssueEditMode) return;

      this.logTubeUpdate(index);
    },
    initSelectCategoryWatcher() {
      this.$watch(
        () => this.selectCategory,
        (newSelections) => {
          this.handleSelectCategoryChange(newSelections);
        },
        { deep: true }
      );
    },
    handleSelectCategoryChange(newSelections) {
      if (this.suppressWatcher) return;

      newSelections.forEach((selectedTests, index) => {
        if (!this.hasUserInteracted[index]) return;

        // Don't auto-flip an explicit recollection status (Not Collected /
        // Hemolyzed). Those intentionally keep their tests, so re-deriving the
        // status from the selection length would wrongly move them to Collected.
        if (this.isRecollectionStatus(this.dropdownStatus[index])) return;

        const tube = this.tubeInfo[index];
        const totalOptions = this.getTotalDropdownOptions(tube);
        const allSelected = selectedTests.length === totalOptions;

        if (selectedTests.length === 0) {
          this.dropdownStatus[index] = this.TUBE_STATUS.NOT_COLLECTED;
          //this.selectErrors[index] = "Test tubes not collected";
        } else {
          this.selectErrors[index] = "";
          this.dropdownStatus[index] = allSelected
            ? this.TUBE_STATUS.COLLECTED
            : totalOptions > 1
            ? this.TUBE_STATUS.PARTIALLY_COLLECTED
            : this.TUBE_STATUS.COLLECTED;
        }
      });

      this.emitTubeData();
    },

    isRecollectionStatus(status) {
      return (
        status === this.TUBE_STATUS.NOT_COLLECTED ||
        status === this.TUBE_STATUS.HEMOLYZED
      );
    },
    getDownloadUrl(imageUrl) {
      if (!imageUrl) return "";
      try {
        const decodedUrl = decodeURIComponent(imageUrl);
        const path = decodedUrl.split(".com/")[1];
        if (!path) return decodedUrl;
        
        const storeUrl =
          this.$store.getters["testTubesImageGeneration/getDownloadUrlByPath"](
            path
          );
        return storeUrl || decodedUrl;
      } catch (e) {
        console.error("Failed to decode URL:", e);
        return imageUrl;
      }
    },
    getAllTestOptions(tube) {
      // Flatten all titles from all testGroups into a single array of strings
      return tube.tests.flatMap((test) => test.title);
    },
    normalizeTestName(testName) {
      return String(testName || "").trim().toLowerCase();
    },
    getCollectedTestsLookup() {
      return new Set(
        (this.getCaseInformation?.samples_with_issues?.collected_tests || [])
          .filter(Boolean)
          .map((testName) => this.normalizeTestName(testName))
      );
    },
    getSelectableTestsForTube(tube) {
      const disabledTests = this.getDisabledTestsForTube(tube);
      return this.getAllTestOptions(tube).filter((test) => !disabledTests.has(test));
    },
    isTestDisabled(tube, testName) {
      return this.getDisabledTestsForTube(tube).has(testName);
    },
    isTestInhouseOtherLab(tube, testName) {
      const restrictedCodes = this.disabledLabOrderCodes;
      if (!restrictedCodes.size) return false;
      const test = (tube?.tests || []).find((t) => t?.title === testName);
      return Boolean(test?.lab_order_code) && restrictedCodes.has(test.lab_order_code);
    },
    getInhouseOtherLabTests(tube) {
      const restrictedCodes = this.disabledLabOrderCodes;
      if (!restrictedCodes.size) return [];
      return (tube?.tests || [])
        .filter((t) => t?.lab_order_code && restrictedCodes.has(t.lab_order_code))
        .map((t) => t.title);
    },
    getTestOptionLabel(tube, testName) {
      if (this.isTestInhouseOtherLab(tube, testName)) {
        return `${testName} (In-house at another lab)`;
      }
      return this.isTestDisabled(tube, testName)
        ? `${testName} (Unavailable)`
        : testName;
    },
    getDisabledTestsForTube(tube) {
      const issues = this.getCaseInformation?.samples_with_issues || [];
      const tubeName = (tube?.tube_name || '').toLowerCase().trim();
      const editablePendingTests = this.getEditablePendingTestsForTube(tube);
      const collectedTests = this.getCollectedTestsLookup();

      // Filter issues for this tube only
      const tubeIssues = issues.filter(
        (issue) => (issue?.tube_name || '').toLowerCase().trim() === tubeName
      );

      // For each test name, record the last occurrence's status and collected_tests (later entries overwrite earlier)
      const lastStatusMap = {};
      const collectedTestsInIssue = {};
      tubeIssues.forEach((issue) => {
        (issue.tests || []).forEach((testName) => {
          lastStatusMap[testName] = issue.status;
          // If status is APPROVED and test is in collected_tests, mark it as collected
          if ((issue.status || '').toUpperCase() === 'APPROVED') {
            collectedTestsInIssue[testName] = (issue.collected_tests || []).includes(testName);
          }
        });
      });

      // A test is disabled if its last status is NOT 'REJECTED'
      const disabled = new Set();
      Object.entries(lastStatusMap).forEach(([testName, status]) => {
        const statusUpper = (status || '').toUpperCase();
        const isApprovedWithCollected = statusUpper === 'APPROVED' && collectedTestsInIssue[testName];
        
        if (
          statusUpper !== 'REJECTED' &&
          !isApprovedWithCollected &&
          !editablePendingTests.has(testName) &&
          !collectedTests.has(this.normalizeTestName(testName))
        ) {
          disabled.add(testName);
        }
      });

      // Disable tests whose lab-order code is restricted for the current lab
      // (DLW-TX / DLW-HOU block the full list, DLW blocks only DLW_UIBC).
      const restrictedCodes = this.disabledLabOrderCodes;
      if (restrictedCodes.size) {
        (tube?.tests || []).forEach((test) => {
          if (test?.lab_order_code && restrictedCodes.has(test.lab_order_code)) {
            disabled.add(test.title);
          }
        });
      }

      return disabled;
    },
    getEditablePendingTestsForTube(tube) {
      if (!this.isPendingIssueEditMode) {
        return new Set();
      }

      const tubeName = String(tube?.tube_name || "").trim().toLowerCase();
      const pendingState = this.pendingIssueStateByTube[tubeName];
      return pendingState?.tests || new Set();
    },
    areAllTestsDisabled(tube) {
      const allTests = this.getAllTestOptions(tube);
      if (allTests.length === 0) return false;
      const disabledTests = this.getDisabledTestsForTube(tube);
      return allTests.every((test) => disabledTests.has(test));
    },
    getTotalDropdownOptions(tube) {
      return this.getSelectableTestsForTube(tube).length;
    },
    async handleSelectionChange(index) {
      this.hasUserInteracted[index] = true;

      // Pending-issue edit mode has its own dedicated MLX update flow.
      if (this.isPendingIssueEditMode) {
        this.validateSelection(index);
        this.emitTubeData();
        this.handlePendingIssueSelectionChange(index);
        return;
      }

      this.logTubeUpdate(index);
    },
    // Logs the tube selection/status change to the backend in the background.
    // No confirmation popup — the update is applied immediately.
    async logTubeUpdate(index) {
      const tube = this.tubeInfo[index];
      if (!tube) return;

      const selectedTests = [...(this.selectCategory[index] || [])];
      this.validateSelection(index);
      this.emitTubeData();

      try {
        await this.$store.dispatch("orders/logTestTubeSelection", {
          order_id: this.$route.params.order_id,
          tube_id: tube._id,
          tube_name: tube.tube_name,
          selected_tests: selectedTests,
          status: this.dropdownStatus[index],
        });
      } catch (error) {
        console.error("Error logging tube selection:", error);
      }
    },
    validateSelection(index) {
      if (this.selectCategory[index].length === 0) {
        this.selectErrors[index] = "Test tubes not collected";
      } else {
        this.selectErrors[index] = "";
      }
      this.emitTubeData();
    },
    emitTubeData() {
      // Debounce emit to reduce parent re-renders
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = setTimeout(() => {
        this.$emit("update-tube-info", this.selectedTubeInformation);
      }, 150); // 150ms debounce
    },
    getRowTests(tube, index) {
      const selectedTests = this.selectCategory[index] || [];
      if (selectedTests.length > 0) {
        return selectedTests;
      }
      return this.getSelectableTestsForTube(tube);
    },
    enablePendingIssueEdit() {
      if (
        !this.isMlxRecollectionAllowed ||
        !this.hasEditablePendingIssues ||
        this.isEditActionLoading
      ) {
        return;
      }

      this.isPendingIssueEditMode = true;
      this.applyPendingIssueSelections();
    },
    cancelPendingIssueEdit() {
      if (this.isEditActionLoading) {
        return;
      }

      this.isPendingIssueEditMode = false;
      this.pendingIssueEditSnapshot = {};
      // Cancelling the pending-issue edit should reset back to defaults, not
      // preserve the temporary pending-edit selections.
      this.initializeTubeSelections(this.tubeInfo, { preserve: false });
      this.emitTubeData();
    },
    applyPendingIssueSelections() {
      this.suppressWatcher = true;

      this.pendingIssueEditSnapshot = {};

      this.tubeInfo.forEach((tube, index) => {
        const tubeName = String(tube?.tube_name || "").trim().toLowerCase();
        const pendingState = this.pendingIssueStateByTube[tubeName];

        if (!pendingState) {
          return;
        }

        const pendingTests = Array.from(pendingState.tests).filter((test) =>
          this.getAllTestOptions(tube).includes(test)
        );

        this.pendingIssueEditSnapshot[tubeName] = {
          tests: [...pendingTests],
          issueIds: pendingState.issues.map((issue) => issue?._id).filter(Boolean),
        };
        this.selectCategory.splice(index, 1, pendingTests);
        this.dropdownStatus.splice(
          index,
          1,
          pendingState.reason || this.TUBE_STATUS.NOT_COLLECTED
        );
        this.selectErrors.splice(index, 1, "");
        this.hasUserInteracted.splice(index, 1, false);
      });

      this.suppressWatcher = false;
      this.emitTubeData();
    },
    async handlePendingIssueSelectionChange(index) {
      const tube = this.tubeInfo[index];
      if (!tube || this.isEditActionLoading) {
        return;
      }

      const payload = this.buildPendingIssueEditPayload(tube, index);
      if (!payload) {
        return;
      }

      try {
        this.editLoadingText =
          payload.reverts.length && !payload.additions.length
            ? "Reverting..."
            : payload.additions.length && !payload.reverts.length
            ? "Adding..."
            : "Updating...";
        this.isEditActionLoading = true;
        await this.$store.dispatch("orders/editMlxOrderSample", {
          order_id: this.currentOrderId,
          payload,
        });
        successNotification("Sample issue updated successfully");
        window.location.reload();
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          "Failed to update sample issue";
        errorNotification(message);
      } finally {
        this.isEditActionLoading = false;
      }
    },
    buildPendingIssueEditPayload(tube, index) {
      const tubeNameKey = String(tube?.tube_name || "").trim().toLowerCase();
      const pendingState = this.pendingIssueStateByTube[tubeNameKey];
      if (!pendingState) {
        return null;
      }

      const baselineSelection = new Set(
        this.pendingIssueEditSnapshot[tubeNameKey]?.tests || []
      );
      const selectedTests = this.selectCategory[index] || [];
      const selectedSet = new Set(selectedTests);

      const reverts = pendingState.issues
        .map((issue) => {
          const removedTests = (issue?.tests || []).filter(
            (test) => baselineSelection.has(test) && !selectedSet.has(test)
          );

          if (!issue?._id || removedTests.length === 0) {
            return null;
          }

          return {
            sample_issue_id: issue._id,
            tests: removedTests,
          };
        })
        .filter(Boolean);

      const addedTests = selectedTests.filter((test) => !baselineSelection.has(test));
      const additions = addedTests.length
        ? [
            {
              tube_name: tube?.tube_name || "",
              tests: addedTests,
              reason:
                this.dropdownStatus[index] ||
                pendingState.reason ||
                this.TUBE_STATUS.NOT_COLLECTED,
            },
          ]
        : [];

      if (reverts.length === 0 && additions.length === 0) {
        return null;
      }

      return {
        reverts,
        additions,
      };
    },
    handleRecollectionAll() {
      if (!this.isMlxRecollectionAllowed) {
        errorNotification("Re collection is only available for MLX orders");
        return;
      }

      if (this.isPendingIssueEditMode) {
        errorNotification("Please cancel edit mode before re collection");
        return;
      }

      if (!this.currentOrderId || !this.hasValidTubes) {
        errorNotification("Please select tests for at least one tube");
        return;
      }

      // Validate: only 'Not Collected' or 'Hemolyzed' statuses allowed for recollection
      const invalidTubes = this.tubeInfo.filter((tube, index) => {
        const selectedTests = this.selectCategory[index] || [];
        if (selectedTests.length === 0) return false;
        const status = this.dropdownStatus[index];
        return (
          status !== this.TUBE_STATUS.NOT_COLLECTED &&
          status !== this.TUBE_STATUS.HEMOLYZED
        );
      });

      if (invalidTubes.length > 0) {
        const tubeNames = invalidTubes.map((t) => t.tube_name).join(", ");
        errorNotification(
          `Re collection is only allowed for tubes with status "Not Collected" or "Hemolyzed". Please update status for: ${tubeNames}`
        );
        return;
      }

      // Build preview data for confirmation popup
      this.recollectionPreview = this.tubeInfo
        .map((tube, index) => {
          const selectedTests = this.selectCategory[index] || [];
          if (selectedTests.length === 0) return null;
          return {
            tubeName: tube?.tube_name || "",
            status: this.dropdownStatus[index] || this.TUBE_STATUS.NOT_COLLECTED,
            tests: selectedTests,
          };
        })
        .filter(Boolean);

      this.showRecollectionConfirm = true;
    },
    async confirmRecollection() {
      try {
        const payload = this.recollectionPreview.map((item) => ({
          tube_name: item.tubeName,
          tests: item.tests,
          reason: item.status,
        }));

        this.isRecollectionLoading = true;
        this.showRecollectionConfirm = false;
        await this.$store.dispatch("orders/updateSampleRecollection", {
          order_id: this.currentOrderId,
          payload,
        });
        this.showSuccessAndReload("Recollection samples updated successfully");
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          "Failed to update samples for re collection";
        errorNotification(message);
      } finally {
        this.isRecollectionLoading = false;
      }
    },
    cancelRecollectionConfirm() {
      this.showRecollectionConfirm = false;
      window.location.reload();
    },
    async clearTubeTests(index) {
      this.selectCategory.splice(index, 1, []);
      this.hasUserInteracted[index] = true;
      this.validateSelection(index);
      this.emitTubeData();

      if (this.isPendingIssueEditMode) return;

      this.logTubeUpdate(index);
    },
    showSuccessAndReload(message) {
      successNotification(message, "Success", 1800);
      setTimeout(() => {
        window.location.reload();
      }, 900);
    },
    async handleRecollection(tube, index) {
      try {
        if (!this.isMlxRecollectionAllowed) {
          errorNotification("Re collection is only available for MLX orders");
          return;
        }

        const tubeName = (tube?.tube_name || "").toLowerCase().trim();
        const isDisabled =
          !tubeName ||
          !this.currentOrderId ||
          this.areAllTestsDisabled(tube);

        if (isDisabled) {
          return;
        }

        const payload = {
          tube_name: tube?.tube_name || "",
          tests: this.getRowTests(tube, index),
          reason: this.dropdownStatus[index] || this.TUBE_STATUS.NOT_COLLECTED,
        };

        if (!payload.tube_name || payload.tests.length === 0) {
          errorNotification("Tube name and tests are required for re collection");
          return;
        }

        this.isRecollectionLoading = true;
        await this.$store.dispatch("orders/updateMlxOrderSample", {
          order_id: this.currentOrderId,
          payload,
        });
        this.showSuccessAndReload("Recollection samples updated successfully");
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          "Failed to update sample for re collection";
        errorNotification(message);
      } finally {
        this.isRecollectionLoading = false;
      }
    },
    normalizeAckStatus(status) {
      return status === "RECEIVED" ? "RECEIVED" : "NOT_RECEIVED";
    },
    getAckByText(tube) {
      if (!tube?.acknowledged_by && !tube?.acknowledged_at && !tube?.lab) return "";
      const parts = [];
      if (tube.acknowledged_by) parts.push(`${tube.acknowledged_by}`);
      if (tube.acknowledged_at) parts.push(`at ${tube.acknowledged_at}`);
      if (tube.lab) parts.push(`(${tube.lab})`);
      return parts.join(" ");
    },
    async fetchAllSendoutLabs() {
      await this.$store.dispatch("labs/fetchAllLabs", {
        get_all: true,
        for_sendout: true,
      });
    },
    onAckStatusChange() {
      this.ackPendingConfirmation = this.hasAckChanges;
      this.$emit("ack-pending-confirmation", this.ackPendingConfirmation);
      this.emitTubeData();
    },
    async confirmAcknowledgment() {
      try {
        const routeOrderId = this.$route.params.order_id;
        if (!routeOrderId) return;

        const payload = this.tubeInfo.map((tube, index) => ({
          order_id: routeOrderId,
          tube_name: tube.tube_name,
          status: this.ackStatus[index],
          order_codes: [...(this.selectCategory[index] || [])],
          sendout_lab: this.sendoutLab[index] || "",
        }));

        this.isAckSaving = true;
        await this.$store.dispatch("orders/updateSampleAcknowledgement", {
          order_id: routeOrderId,
          payload,
        });
        this.ackPendingConfirmation = false;
        this.ackOriginalStatus = [...this.ackStatus];
        this.$emit("ack-pending-confirmation", false);
        this.emitTubeData();
        this.showSuccessAndReload("Sample acknowledgement updated successfully");
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          "Failed to update sample acknowledgement";
        errorNotification(message);
      } finally {
        this.isAckSaving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.case-creation__card {
  margin-top: 1px;
  overflow-x: auto;
}
.case-creation__body {
  padding: 0;
}
.missing-tubes-alert {
  margin: 10px 20px 0;
  padding: 10px 12px;
  border: 1px solid #f3c7c7;
  border-left: 4px solid #dc5a5a;
  border-radius: 6px;
  background: #fff2f2;
  color: #7d1f1f;
  font-size: 12px;
  line-height: 1.45;

  strong {
    margin-right: 4px;
  }
}
.case-creation__heading .title {
  font-weight: bold;
}
.title-with-icon {
  display: flex;
  align-items: center;
  gap: 10px;
}
.edit-pending-issues-btn {
  border: 0;
  background: #eef2ff;
  padding: 6px 12px;
  min-width: 132px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  gap: 8px;
  border-radius: 999px;
  transition: background-color 0.2s ease, color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    background: #dbe4ff;
  }

  &.is-active {
    background: #1f8f45;
    box-shadow: 0 0 0 2px rgba(31, 143, 69, 0.12);

    .edit-pending-issues-text {
      color: #ffffff;
    }

    .edit-pending-issues-icon {
      filter: brightness(0) invert(1);
    }
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}
.edit-pending-issues-icon {
  width: 18px;
  height: 18px;
  object-fit: contain;
}
.edit-pending-issues-text {
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  color: #243a76;
  text-transform: uppercase;
}
.cancel-pending-issues-btn {
  background: #ffffff;
  min-width: 92px;
  min-height: 34px;
  padding: 5px 10px;
  border: 1px solid #d9dce7;
  border-radius: 8px;
  box-shadow: none;

  &:hover {
    background: #f8faff;
    border-color: #cfd6ea;
  }

  .edit-pending-issues-text {
    color: #243a76;
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0;
  }
}
.tube-scroll-wrapper {
  display: flex;
  flex-direction: column;
  overflow-x: auto;
  width: 100%;
}
.tube-notify-message {
  margin: 12px 20px 4px;
  padding: 8px 12px;
  border-left: 4px solid #f59e0b;
  background: #fff8e7;
  color: #6b4f00;
  font-size: 12px;
  font-weight: 600;
  border-radius: 4px;
}
.tube-header {
  display: flex;
  padding: 5px 20px;
  border-bottom: 1px solid #e7e7f7;
}
.tube-column {
  flex: 1;
  text-align: center;
  font-size: 14px;
  font-weight: bold;
  display: flex;
  align-items: center;
  padding: 5px;
  .test-tube-img-block {
    width: 40px;
    height: 40px;
    padding: 0.5rem;
    border: 1px solid #e7e7f7;
    border-radius: 6px;
  }
  .tube-name {
    font-size: 14px;
    font-weight: 600;
    margin: 0;
  }
  &.img-block {
    column-gap: 1rem;
  }
}
.tube-action-column {
  justify-content: center;
}
.tube-cards-block {
  max-height: 235px;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #c1c1c1;
    border-radius: 4px;
    border: 2px solid transparent;
    background-clip: content-box;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: #a8a8a8;
  }
  .tube-card {
    display: flex;
    padding: 5px 0px;
    margin: 0 20px;
    border-bottom: 1px solid #e7e7f7;
    &:nth-last-child(1) {
      border-bottom: none;
    }
  }
}
.el-select {
  width: 160px !important;
  font-size: 12px;
}
::v-deep(.el-select .el-input__inner) {
  background-color: #f9f9f9;
  color: #000;
  font-weight: 500;
  padding: 5px;
  border-radius: 5px;
  border: 1px solid #f9f9f9;
}
::v-deep(.el-select .el-input__suffix .el-input__icon) {
  color: #000000;
}
::v-deep(.el-select .el-input__inner::placeholder) {
  color: #000 !important;
  font-weight: 500 !important;
}
::v-deep(.el-select__tags-text) {
  color: #000 !important;
  font-weight: 500 !important;
}
.error-text {
  color: #f24a55;
  font-size: 11px;
  margin-top: 4px;
  text-align: left;
}
.inhouse-note {
  color: #b06a00;
  font-size: 11px;
  font-style: italic;
  margin-top: 4px;
  text-align: left;
}
.recollection-chip {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  background: #fdecea;
  color: #c0392b;
  border: 1px solid #f5c6c0;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: default;
}
.recollection-btn {
  background-color: #243c8f;
  border-color: #243c8f;
  color: #ffffff;
  font-weight: 600;
  min-width: 120px;
  border-radius: 8px;
}
.recollection-btn:hover,
.recollection-btn:focus {
  background-color: #1f3278;
  border-color: #1f3278;
  color: #ffffff;
}
.recollection-btn.is-disabled,
.recollection-btn.is-disabled:hover,
.recollection-btn.is-disabled:focus {
  background-color: #ced7eb;
  border-color: #ced7eb;
  color: #7c88a8;
}
.tube-actions-row {
  display: flex;
  justify-content: right;
  align-items: center;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid #e7e7f7;
}
.ack-status-column {
  flex-direction: row;
  justify-content: center;
  flex-wrap: nowrap;
  gap: 6px;
}
.ack-lab-select {
  margin-top: 0;
  width: 160px !important;
}
.recollection-btn-all {
  background-color: #273480;
  border-color: #273480;
  color: #ffffff;
  font-weight: 600;
  font-size: 14px;
  padding: 8px 18px;
  border-radius: 8px;
  min-width: 150px;
}
.recollection-btn-all:hover,
.recollection-btn-all:focus {
  background-color: #273480;
  border-color: #273480;
  color: #ffffff;
}
.recollection-btn-all.is-disabled,
.recollection-btn-all.is-disabled:hover,
.recollection-btn-all.is-disabled:focus {
  background-color: #4f5ba4;
  border-color: #4f5ba4;
  color: #ffffff;
}
.ack-by-column {
  text-align: left;
  .ack-by-text {
    font-size: 12px;
    font-weight: 500;
    color: #2e3a59;
  }
}
.ack-confirm-btn {
  background-color: #273480;
  border-color: #273480;
  color: #ffffff;
  font-weight: 600;
  font-size: 14px;
  padding: 8px 18px;
  border-radius: 8px;
  min-width: 190px;
}
.ack-confirm-btn:hover,
.ack-confirm-btn:focus {
  background-color: #273480;
  border-color: #273480;
  color: #ffffff;
}
.ack-confirm-btn.is-disabled,
.ack-confirm-btn.is-disabled:hover,
.ack-confirm-btn.is-disabled:focus {
  background-color: #4f5ba4;
  border-color: #4f5ba4;
  color: #ffffff;
}
.clear-all-tests-btn {
  display: inline-block;
  margin-top: 4px;
  background: none;
  border: none;
  color: #243a76;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  &:hover {
    color: #f24a55;
  }
}
.recollection-confirm-body {
  padding: 0 6px 6px;
}
.recollection-confirm-msg {
  font-size: 13px;
  color: #333;
  margin-bottom: 16px;
}
.recollection-preview-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 320px;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #eef1f8;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #bcc6df;
    border-radius: 10px;
  }
}
.recollection-preview-item {
  background: #f4f6fb;
  border-radius: 8px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.preview-row {
  display: flex;
  gap: 8px;
  font-size: 13px;
  align-items: flex-start;
}
.preview-label {
  font-weight: 700;
  color: #243a76;
  min-width: 52px;
}
.preview-value {
  color: #333;
  word-break: break-word;
}
.preview-status {
  color: #1f8f45;
  font-weight: 600;
}
.recollection-confirm-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.header-part {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 22px;
  font-weight: 600;
  color: #2f2f2f;
}

.dialog-header-icon {
  width: 18px;
  height: 18px;
}

::v-deep(.recollection-theme-modal .el-dialog) {
  border-radius: 8px;
}

::v-deep(.recollection-theme-modal .el-dialog__header) {
  display: none;
}

::v-deep(.recollection-theme-modal .el-dialog__body) {
  padding: 20px 24px 8px;
}

::v-deep(.recollection-theme-modal .el-dialog__footer) {
  padding: 4px 24px 20px;
}

::v-deep(.recollection-theme-modal .el-white) {
  border: 1px solid #cfd6ea;
  color: #4f4f4f;
  border-radius: 8px;
  min-width: 104px;
}

::v-deep(.recollection-theme-modal .blue-save-button) {
  background: #0a5da6;
  border-color: #0a5da6;
  color: #fff;
  border-radius: 8px;
  min-width: 122px;
}

@media (max-width: 991px) {
  ::v-deep(.recollection-theme-modal .el-dialog) {
    width: 92% !important;
    margin-top: 0 !important;
  }

  .recollection-preview-list {
    max-height: 46vh;
  }

  .header-part {
    font-size: 18px;
  }
}
</style>
